import { collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, orderBy, query, setDoc, updateDoc, where, writeBatch } from "firebase/firestore";
import { BotMessage } from "../models/BotMessage";
import { BotPromptSuggestion } from "../models/BotPromptSuggestion";
import { AskSoribotRequest, AskSoribotResponse, CreateSoribotRequest, GetOfficeNotePagesRequest, GetOfficeNotePagesResponse, GetSoribotByPublicIdRequest, GetSoribotByPublicIdResponse, GetSoribotStatsRequest, GetSoribotStatsResponse, GetSoribotsByRoomItemRequest, GetSoribotsByRoomItemResponse, ONPageItem, RunTestCaseRequest, RunTestCaseResponse, SetSoribotPageRequest, SetSoribotPageResponse, SoribotContentRequest, SoribotContentResponse, UpdateSoribotContentRequest } from "../models/DTO";
import { AIComment, Message } from "../models/Message";
import { PublicStyle, Soribot, SoribotAPIKey, SoribotPage, SoribotSimplified } from "../models/Soribot";
import { BaseSource, DataSourceType, SourceDocument, SourceOfficeNotePage, SourceRoomItem, SourceUploadedFile } from "../models/SoribotSource";
import { SoribotTestCase, SoribotTestRun } from "../models/SoribotTestCase";
import { DocumentLoader } from "../models/local/Types";
import { UUID } from "../models/local/Uid";
import { UIMessage } from "../ui/components/UIMessage";
import { FireClient, getFunc } from "./FireClient";
import { My } from "./My";
import { Util, pojo } from "./Utils/Util";
import { UtilApp } from "./Utils/UtilApp";

export class FireClientBot {
  constructor(private c: FireClient) { }

  async createSoribot(data: CreateSoribotRequest): Promise<Soribot> {
    const r = await getFunc('createSoribot')(data);
    return r.data as Soribot;
  }

  async deleteSoribot(botId: string) {
    await deleteDoc(doc(this.c.store, Soribot.COL, botId));
  }

  async getSoribot(id: string): Promise<Soribot | null> {
    const docSnap = await getDoc(doc(this.c.store, Soribot.COL, id));
    return docSnap.data() as Soribot | null;
  }

  async getSoribotContent(data: SoribotContentRequest): Promise<SoribotContentResponse> {
    const r = await getFunc('getSoribotContent')(data);
    return r.data as SoribotContentResponse;
  }

  async updateSoribot(bot: Soribot) {
    // await updateDoc(doc(this.c.store, Soribot.COL, bot.id), Util.pojo(bot));
    await getFunc('updateSoribot')({ bot: Util.pojo(bot) });
  }

  subscribeToMySoribots(userId: string, handler: (bots: Soribot[]) => void) {
    const q = query(collection(this.c.store, Soribot.COL), where('userIds', 'array-contains', userId));
    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as Soribot));
    });
  }

  subscribeToSourceObjects(soribotId: string, type: DataSourceType, handler: (items: any[]) => void) {
    const q = query(collection(this.c.store, Soribot.COL, soribotId, BaseSource.SUBCOL), where('type', '==', type));
    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as SourceOfficeNotePage[] | SourceUploadedFile[] | SourceRoomItem[]));
    });
  };

  async askSoribot(data: AskSoribotRequest): Promise<AskSoribotResponse> {
    const funcName = My.isLoggedIn() ? 'askSoribot' : 'askSoribotPublic';
    const r = await getFunc(funcName)(data);
    return r.data as AskSoribotResponse;
  }

  async deleteSoribotContent(data: { soribotId: string; }) {
    await getFunc('deleteSoribotContent')(data);
  }

  async getPagesFromOfficeNote(data: GetOfficeNotePagesRequest): Promise<GetOfficeNotePagesResponse> {
    const r = await getFunc('getPagesFromOfficeNote')(data);
    return r.data as GetOfficeNotePagesResponse;
  }

  async getSourceOfficeNotePages(soribotId: string) {
    return await this.getSources(soribotId, 'officenote-page') as SourceOfficeNotePage[];
  }

  async getSourceFiles(soribotId: string) {
    return await this.getSources(soribotId, 'file-bot-upload') as SourceUploadedFile[];
  }

  async getSourceRoomItems(soribotId: string) {
    return await this.getSources(soribotId, 'room-item') as SourceRoomItem[];
  }

  private async getSources(soribotId: string, type: DataSourceType): Promise<SourceOfficeNotePage[] | SourceUploadedFile[] | SourceRoomItem[]> {
    const r = await getDocs(
      query(
        collection(this.c.store, Soribot.COL, soribotId, BaseSource.SUBCOL),
        where('type', '==', type)
      )
    );
    const list = r.docs.map(d => d.data());

    if (type === 'officenote-page')
      return list as SourceOfficeNotePage[];
    else if (type === 'file-bot-upload')
      return list as SourceUploadedFile[];
    else if (type === 'room-item')
      return list as SourceRoomItem[];
    else
      throw new Error(`Unknown type: ${type}`);
  }

  async saveSourceOfficeNotePage(userId: string, soribotId: string, page: ONPageItem) {
    const sourcePage = new SourceOfficeNotePage(soribotId, userId, page.uuid, page.title, page.dt_update ? new Date(page.dt_update * 1000) : null);
    await this.saveSourceObj(sourcePage);
    return sourcePage;
  }

  async saveSourceFiles(soribotId: string, files: SourceUploadedFile[]) {
    const batch = writeBatch(this.c.store);
    files.forEach(f => {
      batch.set(doc(this.c.store, Soribot.COL, soribotId, BaseSource.SUBCOL, f.id), Util.pojo(f));
    });
    await batch.commit();
  }

  async saveSourceDocAndSync(sd: SourceDocument) {
    const func = getFunc('saveSourceDoc');
    await func(Util.pojo(sd));
  }

  async setSourceDocWithFile(data: { sd: SourceDocument, loader: DocumentLoader }) {
    const func = getFunc('setSourceDocWithFile');
    await func(Util.pojo(data));
  }

  async saveSourceObj(sourceObj: BaseSource) {
    await setDoc(doc(this.c.store, Soribot.COL, sourceObj.soribotId, BaseSource.SUBCOL, sourceObj.id), Util.pojo(sourceObj));
  }

  async getSourceObj(soribotId: string, sourceId: string) {
    const d = await getDoc(doc(this.c.store, Soribot.COL, soribotId, BaseSource.SUBCOL, sourceId));
    return d.data() as BaseSource | null;
  }

  async deleteSources(removedSources: BaseSource[]) {
    if (removedSources.length > 0) {
      const batch = writeBatch(this.c.store);
      removedSources.forEach(s => {
        batch.delete(doc(this.c.store, Soribot.COL, s.soribotId, BaseSource.SUBCOL, s.id));
      });
      await batch.commit();
    }
  }

  async updateSoribotContent(data: UpdateSoribotContentRequest) {
    await getFunc('updateSoribotContent')(Util.pojo(data));
  }

  async deleteSource(source: BaseSource) {
    await deleteDoc(doc(this.c.store, Soribot.COL, source.soribotId, BaseSource.SUBCOL, source.id));
  }

  async getSoribotsByRoomItem(data: GetSoribotsByRoomItemRequest): Promise<GetSoribotsByRoomItemResponse> {
    const r = await getFunc('getSoribotsByRoomItem')(data);
    return r.data as GetSoribotsByRoomItemResponse;
  }

  // ============================================================

  subscribeToTestCases(soribotId: string, handler: (testcases: SoribotTestCase[]) => void) {
    const q = query(collection(this.c.store, Soribot.COL, soribotId, SoribotTestCase.SUBCOL), orderBy('created', 'desc'));
    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as SoribotTestCase));
    });
  }

  async getTestCases(soribotId: string) {
    const r = await getDocs(
      query(collection(this.c.store, Soribot.COL, soribotId, SoribotTestCase.SUBCOL))
    );
    return r.docs.map(d => d.data() as SoribotTestCase);
  }

  async addTestCase(soribotId: string, title: string) {
    const tc = new SoribotTestCase(UUID.get(), soribotId, title);
    await setDoc(doc(this.c.store, Soribot.COL, soribotId, SoribotTestCase.SUBCOL, tc.id), Util.pojo(tc));
    return tc;
  }

  async saveTestCase(testcase: SoribotTestCase) {
    await setDoc(doc(this.c.store, Soribot.COL, testcase.soribotId, SoribotTestCase.SUBCOL, testcase.id), Util.pojo(testcase));
  }

  async deleteTestCase(testcase: SoribotTestCase) {
    await deleteDoc(doc(this.c.store, Soribot.COL, testcase.soribotId, SoribotTestCase.SUBCOL, testcase.id));
  }

  // ============================================================

  subscribeToTestRuns(soribotId: string, handler: (testcases: SoribotTestRun[]) => void) {
    const q = query(collection(this.c.store, Soribot.COL, soribotId, SoribotTestRun.COL), orderBy('created', 'desc'));
    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as SoribotTestRun));
    });
  }

  async runTestCase(data: RunTestCaseRequest): Promise<RunTestCaseResponse> {
    const r = await getFunc('runTestCase')(Util.pojo(data));
    return r.data as RunTestCaseResponse;
  }

  async deleteTestRun(soribotId: string, testRunId: string) {
    await deleteDoc(doc(this.c.store, Soribot.COL, soribotId, SoribotTestRun.COL, testRunId));
  }

  async evaluateTestRun(soribotId: string, testRunId: string | undefined) {
    if (testRunId) {
      await getFunc('evaluateTestRun')({ soribotId, testRunId });
    }
  }

  async stopProcess(testRun: SoribotTestRun) {
    await updateDoc(doc(this.c.store, Soribot.COL, testRun.testCase.soribotId, SoribotTestRun.COL, testRun.id), { isRunning: false, isEvaluating: false });
  }

  async askChatGPT(prompt: string): Promise<string> {
    const r = await getFunc(`askChatGPT`)({ prompt: prompt });
    return r.data as string;
  }

  async askSoribotIfMentioned(msg: Message, soribots: Soribot[], messages: UIMessage[]) {
    if (!msg.text?.includes('(user:ai-soribot'))
      return;

    let history = '';

    if (msg.parentMessageId) {
      const parentMsg = messages.find(m => m.msg.id === msg.parentMessageId);
      if (parentMsg) {
        history = UtilApp.getThreadText(parentMsg);
      }
    }
    const askedSoribots = soribots.filter(b => msg.text?.includes(b.id));
    const comments: AIComment[] = [];
    const prompt = this.removeUserMention(msg.text);
    const finalPrompt = history ? `${history}. \n\n${prompt}` : prompt;
    const msgDoc = doc(this.c.store, Message.COL, msg.id);
    updateDoc(msgDoc, { inProgress: true });

    for (let i = 0; i < askedSoribots.length; i++) {
      const bot = askedSoribots[i];
      const r = await this.askSoribot({ prompt: finalPrompt, soribotId: bot.id, suggestionId: null });
      comments.push(new AIComment(r.answer, r.botMessageId, bot.name, bot.id, bot.imageUrl))
      const isProgress = i < askedSoribots.length - 1;
      await updateDoc(msgDoc, pojo({ comments: comments, inProgress: isProgress }));
    }
  }

  removeUserMention(text: string) {
    // Regex pattern to match the format @[Name](user:ai-soribot-<dynamic-id>)
    const pattern = /@\[.*?\]\(user:ai-soribot-.*?\)/g;
    return text.replace(pattern, '').trim();
  }

  async getSoribotByPublicId(data: GetSoribotByPublicIdRequest): Promise<GetSoribotByPublicIdResponse> {
    const r = await getFunc('getSoribotByPublicId')(data);
    return r.data as GetSoribotByPublicIdResponse;
  }

  async setSoribotPage(data: SetSoribotPageRequest) {
    const r = await getFunc('setSoribotPage')(Util.pojo(data));
    return r.data as SetSoribotPageResponse;
  }

  async getSoribotPage(soribotId: string) {
    const d = await getDoc(doc(this.c.store, SoribotPage.COL, soribotId));
    return d.data() as SoribotPage | null;
  }

  async createSoribotAPIKey(soribotId: string) {
    const r = await getFunc('createSoribotAPIKey')({ soribotId });
    return r.data as string;
  }

  async getSoribotAPIKeys(soribotId: string): Promise<SoribotAPIKey[]> {
    const r = await getFunc('getSoribotAPIKeys')({ soribotId });
    return r.data as SoribotAPIKey[];
  }

  async deleteSoribotAPIKey(soribotId: string, keyId: string) {
    await getFunc('deleteSoribotAPIKey')({ soribotId, keyId });
  }

  async getBotMessage(botMessageId: string): Promise<BotMessage> {
    const d = await getDoc(doc(this.c.store, BotMessage.COL, botMessageId));
    return d.data() as BotMessage;
  }

  async getPromptSuggestions(soribotId: string, publicAPI: boolean = false): Promise<BotPromptSuggestion[]> {
    const funcName = publicAPI ? 'getPromptSuggestionsPublic' : 'getPromptSuggestions'
    const r = await getFunc(funcName)({ soribotId });
    return r.data as BotPromptSuggestion[];
  }

  async getBotPromptSuggestion(soribotId: string, suggestionId: string): Promise<BotPromptSuggestion | undefined> {
    const d = await getDoc(doc(this.c.store, Soribot.COL, soribotId, BotPromptSuggestion.SUBCOL, suggestionId));
    return d.data() as BotPromptSuggestion | undefined;
  }

  subscribeToPromptSuggestions(soribotId: string, handler: (list: BotPromptSuggestion[]) => void) {
    const q = query(collection(this.c.store, Soribot.COL, soribotId, BotPromptSuggestion.SUBCOL), orderBy('updated', 'desc'));
    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as BotPromptSuggestion));
    });
  }

  async saveSuggestion(sug: BotPromptSuggestion) {
    sug.updated = new Date();
    await setDoc(doc(this.c.store, Soribot.COL, sug.botId, BotPromptSuggestion.SUBCOL, sug.id), Util.pojo(sug));
  }

  async saveSuggestions(sugs: BotPromptSuggestion[]) {
    const batch = writeBatch(this.c.store);
    sugs.forEach(sug => {
      sug.updated = new Date();
      batch.set(doc(this.c.store, Soribot.COL, sug.botId, BotPromptSuggestion.SUBCOL, sug.id), Util.pojo(sug));
    });
    await batch.commit();
  }

  async deleteSuggestion(sug: BotPromptSuggestion) {
    await deleteDoc(doc(this.c.store, Soribot.COL, sug.botId, BotPromptSuggestion.SUBCOL, sug.id));
  }

  async deleteAllSuggestions(soribotId: string) {
    const r = await getDocs(query(collection(this.c.store, Soribot.COL, soribotId, BotPromptSuggestion.SUBCOL)));
    const batch = writeBatch(this.c.store);
    r.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
  }

  async getSoribotStats(data: GetSoribotStatsRequest): Promise<GetSoribotStatsResponse> {
    const r = await getFunc('getSoribotStats')(pojo(data));
    return r.data as GetSoribotStatsResponse;
  }

  async getOpenAIKeyLast4(workspaceId: string): Promise<string> {
    const r = await getFunc('getOpenAIKeyLast4')({ workspaceId });
    return r.data as string;
  }

  async setOpenAIKey(workspaceId: string, key: string) {
    const r = await getFunc('setOpenAIKey')({ workspaceId, key });
    return r.data as string;
  }

  async deleteOpenAIKey(workspaceId: string) {
    const r = await getFunc('deleteOpenAIKey')({ workspaceId });
    return r.data as string;
  }

  async getWorkspaceBots(workspaceId: string): Promise<Soribot[]> {
    const r = await getFunc('getWorkspaceBots')({ workspaceId });
    return r.data as Promise<Soribot[]>;
  }

  async saveSuggestionsAsKnowledge(soribotId: string) {
    await getFunc('saveSuggestionsAsKnowledge')({ soribotId });
  }

  async updatePageStyle(soribotId: string, pageStyle: PublicStyle) {
    await updateDoc(doc(this.c.store, Soribot.COL, soribotId), { style: pageStyle });
  }
}
